import * as _ from "lodash";

import * as ModelStructure from "../modelStructure/modelStructureExtension";

export type Attribute = { Key: string, Value: string };
export type AttributeBlock = { Name: string, Attributes: Attribute[], Blocks: AttributeBlock[] };

const namedAttributes = ["NAME", "DESC", "FUNC", "TYPE", "HEAD CONN.", "TAIL CONN.", "DTXR", "DTXS", "STANDARD", "DTXT", "MTXX"];
const psetPrefix = "AVEVA_Pset :";
const partPrefixes = ["BRANCH ", "VALVE ", "INSTRUMENT "];

/**
 * A property panel that displays properties according to a filtering criteria.
 */
export class PdmsAttrPanel extends Autodesk.Viewing.Extensions.ViewerPropertyPanel {

    private viewer: Autodesk.Viewing.GuiViewer3D;
    private attributes: AttributeBlock[];
    private mapping: ModelStructure.Mapping;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D) {
        super(viewer);
        this.viewer = viewer;
        this.setTitle("PDMS attributes", null);
    }

    /**
      * Called by the superclass on node selection. Tries to find PDMS attributes according to the node's IFC name and the
     * property sets (psets) in the node properties. Also recursively fetches the parent element attributes.
      * @param nodeId Selected node.
      */   
    async requestNodeProperties(nodeId: any) {
        const props = await this.getPropertiesFromModel(nodeId);

        let attrProps = this.getAttributesByElementName(props);

        const psets = this.getPsets(props);

        attrProps = attrProps.concat(this.getAttributesByPsets(props, psets));

        //attrProps = attrProps.concat(this.getParentAttributes(props));

        this.setProperties(attrProps);
        this.resizeToContent(null);
    }

    private getAttributesByElementName(props: Autodesk.Viewing.Property[]) {
        let attrProps = [];
        const nameProp = _.find(props, p => p.displayCategory === "IFC" && p.displayName === "NAME");
        let name = null;
        if (nameProp != null) {
            name = nameProp.displayValue;
            const block = this.findBlockByNodeName(name);
            if (block != null) {
                const attrs = _.filter(block.Attributes, this.isPicked);
                attrProps = attrProps.concat(attrs.map(a => this.attrToProp(a, name)));
            }
        }
        return attrProps;
    }

    private getPsets(props: Autodesk.Viewing.Property[]) {
        const categories = _.uniq(props.map(p => p.displayCategory));
        return _.filter(categories, c => _.startsWith(c, psetPrefix)).map(p => {
            let pset = p.replace(psetPrefix, "");
            pset = this.removePartPrefix(pset);
            return pset;
        });
    }

    private getAttributesByPsets(props: Autodesk.Viewing.Property[], psets) {
        let attrProps = [];
        for (var pset of psets) {
            if (`/${name}` === pset) {
                continue;
            }
            const block = _.find(this.attributes, a => a.Name === `/${pset}` || a.Name === pset);
            if (block != null) {
                const attrs = _.filter(block.Attributes, this.isPicked);
                attrProps = attrProps.concat(attrs.map(a => this.attrToProp(a, pset)));
            }
        }
        return attrProps;
    }

    private getParentAttributes(props: Autodesk.Viewing.Property[]) {
        let attrProps = [];
        const guidProp = _.find(props, p => p.displayCategory === "IFC" && p.displayName === "GLOBALID");
        if (this.mapping != null && guidProp != null) {
            const guid = guidProp.displayValue;
            const mapping = _.find(this.mapping.items, m => m.guid == guid);
            if (mapping != null) {
                let parentGuid = mapping.parent;

                while (parentGuid != null) {
                    const parentMapping = _.find(this.mapping.items, m => m.guid == parentGuid);
                    const block = this.findBlockByNodeName(parentMapping.name);
                    if (block != null) {
                        const attrs = _.filter(block.Attributes, this.isPicked);
                        attrProps = attrProps.concat(attrs.map(a => this.attrToProp(a, parentMapping.name)));
                    }
                    parentGuid = parentMapping.parent;
                }
            }
        }
        return attrProps;
    }

    private findBlockByNodeName(name: string) {
        return _.find(this.attributes, a => a.Name === `/${name}` || a.Name === name || a.Name === `/${name.replace("/BODY", "")}`);
    }

    private removePartPrefix(pset: string) {
        for (const prefix of partPrefixes) {
            if (_.startsWith(pset, prefix)) {
                return pset.replace(prefix, "");
            }
        }
        return pset;
    }

    private isPicked(attribute: Attribute): boolean {
        return _.startsWith(attribute.Key, ":") || _.some(namedAttributes, na => na === attribute.Key);
    }

    private attrToProp(attribute: Attribute, displayCategory: string): Autodesk.Viewing.Property {
        return {
            attributeName: attribute.Key,
            displayCategory: displayCategory,
            displayName: attribute.Key,
            displayValue: attribute.Value,
            hidden: false,
            type: 0,
            units: "",
        };
    }

    /**
     * The getter is implemented here to decouple the property finder from the viewer.
     */
    private getPropertiesFromModel = (nodeId): Promise<Autodesk.Viewing.Property[]> => {
        return new Promise((resolve, reject) => {
            this.viewer.model.getProperties(
                nodeId,
                result => resolve(result.properties),
                error => reject(error));
        });
    }

    setAttributes(attributes) {
        this.attributes = attributes;
    }

    setModelStructureMapping(mapping: ModelStructure.Mapping) {
        this.mapping = mapping;
    }
}